import React, { FC } from 'react';

import { Box, styled } from '@mui/material';

import { MetaText } from '~/components/UI/Texts';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { TINY_MARGIN_PX } from '~/theme';
import { Pricing } from '~/types';
import { Tag } from '~/ui/components/Tag/Tag';
import getSymbolFromCurrency from '~/ui/util/getSymbolFromCurrency';

function parsePrice(price: string) {
  const [priceValue, currencyCode] = price.split(' ');

  const [intValue, decimalValue] = priceValue.split('.');
  const currencySymbol = getSymbolFromCurrency(currencyCode);

  return {
    intValue: parseInt(intValue, 0),
    decimalValue,
    currencySymbol,
  };
}

interface PlanPriceProps {
  taxLabel: string;
  price: NonNullable<Pricing['chargebee_plan_info']>['price'];
  discounted: boolean;
  periodLabel: string;
}

const PlanPrice: FC<PlanPriceProps> = ({ taxLabel, price, discounted, periodLabel }) => {
  const { intValue, decimalValue, currencySymbol } = parsePrice(price ?? '');
  const nonZeroDecimal = decimalValue === '00' ? '' : decimalValue;

  const { t } = useTenantTranslation();

  return (
    <Box display="flex" columnGap={TINY_MARGIN_PX}>
      {price && (
        <>
          <StyledCurrency>{currencySymbol}</StyledCurrency>
          <StyledPrice>
            {intValue}
            {nonZeroDecimal && <StyledPriceDecimal>.{nonZeroDecimal}</StyledPriceDecimal>}
          </StyledPrice>
          <StyledPriceMetadata>
            {discounted && (
              <StyledDiscountRow>
                <Tag variant="shrink">{t('common.20off')}</Tag>
              </StyledDiscountRow>
            )}
            <MetaText>
              / {periodLabel}. {taxLabel}
            </MetaText>
          </StyledPriceMetadata>
        </>
      )}
    </Box>
  );
};

export default PlanPrice;

const StyledCurrency = styled('span')`
  font-size: 24px;
  line-height: 28px;
  font-weight: 700;
  align-self: flex-end;
  min-width: 19px;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 28px;
    line-height: 34px;
  }
`;
const StyledPrice = styled('span')`
  font-size: 64px;
  line-height: 68px;
  font-weight: 700;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    text-align: left;
    font-size: 72px;
    line-height: 72px;
  }
`;
const StyledPriceDecimal = styled('span')`
  font-size: 32px;
  line-height: 28px;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 36px;
    line-height: 32px;
  }
`;
const StyledDiscountRow = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${TINY_MARGIN_PX};
`;
const StyledPriceMetadata = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${TINY_MARGIN_PX};
`;
